import React, { useEffect, useState } from 'react';
import api from '../api';
import { loadLocalHistory, normalizeHistoryItems, saveLocalHistory } from '../historyStorage';

export default function GameHistory() {
  const [history, setHistory] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedReason, setSelectedReason] = useState(null);

  useEffect(() => {
    const fetchHistory = async () => {
      const token = localStorage.getItem('token');
      const localHistory = loadLocalHistory(token);
      if (localHistory.length > 0) {
        setHistory(localHistory);
      }

      try {
        const res = await api.get('/api/history', {
          headers: { Authorization: `Bearer ${token}` },
        });
        const mergedHistory = normalizeHistoryItems([...(res.data.items || []), ...localHistory]);
        setHistory(saveLocalHistory(mergedHistory, token));
        setError('');
      } catch (err) {
        console.error('[GameHistory] error', err);
        if (localHistory.length === 0) {
          setError('Could not load past recommendations.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, []);

  const formatValue = (value) => {
    const normalized = String(value || '').trim();
    return normalized || 'Not recorded';
  };

  const formatOverride = (value) => {
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    const normalized = String(value || '').trim();
    return normalized || 'No';
  };

  return (
    <section className="surface-card">
      <div className="flex flex-col gap-3 border-b border-stone-200 pb-6 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="eyebrow">Review Called Plays</p>
          <h2 className="mt-3 text-4xl font-semibold text-brand-black">Game call review</h2>
          <p className="mt-3 max-w-3xl text-lg leading-8 text-stone-700">
            Use this page to review situations, recommendations, and decision support from earlier
            drives and games.
          </p>
        </div>
        <div className="rounded-lg bg-brand-offwhite px-4 py-3 text-base text-stone-700">
          {loading ? 'Loading...' : `${history.length} calls to review`}
        </div>
      </div>

      {error && history.length === 0 && (
        <p className="mt-6 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-base text-rose-700">
          {error}
        </p>
      )}

      {loading && history.length === 0 && (
        <div className="mt-8 grid gap-4">
          {Array.from({ length: 3 }).map((_, index) => (
            <div key={index} className="h-32 animate-pulse rounded-lg bg-brand-offwhite" />
          ))}
        </div>
      )}

      {(!error || history.length > 0) && !loading && history.length === 0 && (
        <div className="mt-8 rounded-lg border-2 border-dashed border-stone-300 bg-brand-offwhite px-6 py-10 text-center">
          <h3 className="text-2xl font-semibold text-brand-black">No called plays to review yet.</h3>
          <p className="mt-3 text-base text-stone-700">
            Once the team starts using the mobile app for recommendations, those calls will show up
            here for review.
          </p>
        </div>
      )}

      {history.length > 0 && (
        <div className="mt-8 overflow-x-auto rounded-lg border-2 border-stone-200 bg-white">
          <table className="min-w-[1100px] w-full border-collapse text-left text-sm">
            <thead className="bg-brand-offwhite">
              <tr className="border-b border-stone-200">
                <th className="px-4 py-3 font-semibold text-brand-black">Time</th>
                <th className="px-4 py-3 font-semibold text-brand-black">Situation</th>
                <th className="px-4 py-3 font-semibold text-brand-black">Play recommendation</th>
                <th className="px-4 py-3 font-semibold text-brand-black">Coach override</th>
                <th className="px-4 py-3 font-semibold text-brand-black">Actual play called</th>
                <th className="px-4 py-3 font-semibold text-brand-black">Gain/Loss</th>
                <th className="px-4 py-3 font-semibold text-brand-black">Result of play</th>
                <th className="px-4 py-3 font-semibold text-brand-black">Why</th>
              </tr>
            </thead>
            <tbody>
              {history.map((item) => (
                <tr key={`${item.timestamp}-${item.situation || ''}`} className="border-b border-stone-200 align-top last:border-b-0">
                  <td className="whitespace-nowrap px-4 py-4 text-stone-600">
                    {new Date(item.timestamp).toLocaleString()}
                  </td>
                  <td className="min-w-[220px] px-4 py-4 font-semibold text-brand-black">
                    {formatValue(item.situation)}
                  </td>
                  <td className="min-w-[260px] px-4 py-4 text-stone-700">
                    <div><span className="font-semibold text-brand-black">Run:</span> {formatValue(item.run)}</div>
                    <div className="mt-1"><span className="font-semibold text-brand-black">Pass:</span> {formatValue(item.pass)}</div>
                  </td>
                  <td className="px-4 py-4 text-stone-700">{formatOverride(item.coachOverride)}</td>
                  <td className="min-w-[180px] px-4 py-4 text-stone-700">{formatValue(item.actualPlayCalled)}</td>
                  <td className="px-4 py-4 text-stone-700">{formatValue(item.gainLoss)}</td>
                  <td className="min-w-[160px] px-4 py-4 text-stone-700">{formatValue(item.result)}</td>
                  <td className="px-4 py-4">
                    {item.reasoning ? (
                      <button
                        type="button"
                        className="font-semibold text-brand-orange underline"
                        onClick={() => setSelectedReason(item)}
                      >
                        View why
                      </button>
                    ) : (
                      <span className="text-stone-500">Not recorded</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedReason && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="max-w-2xl rounded-lg bg-white px-6 py-6 shadow-xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="eyebrow">Why</p>
                <h3 className="mt-2 text-2xl font-semibold text-brand-black">
                  {formatValue(selectedReason.situation)}
                </h3>
              </div>
              <button
                type="button"
                className="rounded-lg border border-stone-300 px-3 py-2 text-sm font-semibold text-stone-700"
                onClick={() => setSelectedReason(null)}
              >
                Close
              </button>
            </div>
            <p className="mt-5 text-base leading-7 text-stone-700">{selectedReason.reasoning}</p>
          </div>
        </div>
      )}
    </section>
  );
}
